import React from "react";
import { useTranslation } from "react-i18next";
import { ResourceAvatar } from "@common/resourceAvatar";
import { MarkDownContent } from "./markDownContent";
import { Reply } from "./reply";
import { Props } from "./types";
import styles from "./styles.scss";
import { Safeguard } from "@scenes/safeguard";
import { UseCases } from "@planzone/core";
import { TrashIcon } from "@common/trashIcon";
import { useModal } from "@root/client/modal";
import classNames from "classnames";

function Comment({ comment, resources, replies, onReply }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const isPending = comment.uuid.match(/^__pending__/);
  const submissionDate = new Date(comment.createdDate);

  return (
    <div
      className={classNames(styles.comment, {
        [styles.pending]: isPending,
      })}
    >
      <div className={styles.author}>
        <div className={styles.avatar}>
          <ResourceAvatar resource={comment.author} />
        </div>
        <div className={styles.column}>
          <p className={styles.name}>{comment.author.name}</p>
          {!!comment.author.profession ? (
            <p className={styles.role}>{comment.author.profession}</p>
          ) : (
            comment.author.role && (
              <p className={styles.role}>
                {t(
                  `roles.${comment.author.role.name}`,
                  comment.author.role.name,
                )}
              </p>
            )
          )}
        </div>
      </div>
      <div className={styles.main}>
        <p className={styles.submissionDate}>
          {submissionDate.toLocaleDateString()} à {submissionDate.getHours()}h
          {submissionDate.getMinutes()}
        </p>
        <div className={styles.content}>
          {comment.content.trim().startsWith("<") ? (
            <div
              dangerouslySetInnerHTML={{
                __html: MarkDownContent.styleMentions(
                  comment.content,
                  (comment.recipients.length > 0
                    ? comment.recipients
                    : resources
                  )
                    .map(({ name }) => name)
                    .filter((name) => name !== comment.replyTo?.author.name),
                ),
              }}
            />
          ) : (
            <p>
              <MarkDownContent
                content={comment.content}
                replacements={{
                  [MarkDownContent.ExpressionTypes.TEXT]: (text) => (
                    <span>{text}</span>
                  ),
                  [MarkDownContent.ExpressionTypes.RESOURCE]: (text) => (
                    <span style={{ color: "#269FFF" }}>{text}</span>
                  ),
                }}
                resources={
                  comment.recipients.length > 0 ? comment.recipients : resources
                }
              />
            </p>
          )}
          {replies.length > 0 && (
            <div className={styles.replies}>
              {replies.map((reply) => (
                <Reply key={reply.uuid} comment={reply} resources={resources} />
              ))}
            </div>
          )}
        </div>
        <button className={styles.replyButton} onClick={() => onReply(comment)}>
          {t("comment.reply")}
        </button>
      </div>
      <Safeguard useCases={[UseCases.removeComment]} context={{ comment }}>
        {({ canRemoveComment }) =>
          canRemoveComment ? (
            <div
              className={styles.iconBox}
              onClick={() =>
                pushView({
                  key: UseCases.removeComment,
                  data: comment,
                })
              }
            >
              <TrashIcon className={styles.icon} />
            </div>
          ) : (
            <div className={styles.iconBox}>
              <div className={styles.icon} />
            </div>
          )
        }
      </Safeguard>
    </div>
  );
}

export { Comment };
